import type { RepairshoprTicketLineItem, LineItemOverride } from '@techgitter/contracts';
import type { LineItem } from '../types/ticketTypeTemplate';
import { getProductBySku } from '../hooks/useProductCatalog';
import { rsPriceRetail } from './nonChargeablePrice';

/**
 * Diff a form's line items against what RepairShopr already holds.
 *
 * Both line-item editors — the technician card's Line Items section and the
 * Edit Ticket page — need the same answer to "what changed?", so the decision
 * lives here rather than in either caller. The callers still own their own
 * request orchestration (pending flags, scheduled syncs, save-phase gating),
 * which is genuinely different between them.
 *
 * Purely a calculation: it issues no requests and mutates nothing.
 */

export interface LineItemCreate {
  name: string;
  quantity: number;
  price_retail: number;
  description?: string;
  product_id?: number;
  templateKey?: string;
}

export interface LineItemUpdate {
  rsLineItemId: number;
  fields: { quantity?: number; price_retail?: number; description?: string };
}

export interface LineItemEditDiff {
  creates: LineItemCreate[];
  updates: LineItemUpdate[];
  deletes: number[];
  /** Full replacement blob — the endpoint overwrites cr001_line_item_metadata. */
  overrides: LineItemOverride[];
  /** True when nothing needs to be sent at all. */
  isEmpty: boolean;
}

/** RS fields that differ from what RS last told us. Null when nothing differs. */
export function getRsDirtyFields(
  item: LineItem,
  rsSnapshot: Map<number, RepairshoprTicketLineItem>,
): LineItemUpdate['fields'] | null {
  if (item.rsLineItemId == null) return null;
  const rs = rsSnapshot.get(item.rsLineItemId);
  if (!rs) return null;

  const dirty: LineItemUpdate['fields'] = {};
  const rsQuantity = parseFloat(rs.quantity) || 0;
  if (item.quantity !== rsQuantity) dirty.quantity = item.quantity;
  const priceRetail = rsPriceRetail(item);
  if (priceRetail !== rs.price_retail) dirty.price_retail = priceRetail;

  // lineDetail maps to RS description
  const rsDescription = rs.description ?? '';
  if (item.lineDetail !== rsDescription) dirty.description = item.lineDetail;

  return Object.keys(dirty).length > 0 ? dirty : null;
}

/** The SM4RT metadata blob for every line that has an RS id to key it by. */
export function buildOverrides(items: LineItem[]): LineItemOverride[] {
  return items
    .filter(item => item.rsLineItemId != null)
    .map(item => ({
      rsLineItemId: item.rsLineItemId!,
      afterHours: item.afterHours,
      nonChargeable: item.nonChargeable,
      prefixUserBase: item.prefixUserBase,
      category: item.category,
      ...(item.templateKey ? { templateKey: item.templateKey } : {}),
      // RS holds a no-charge line at $0, so its rate is kept here.
      ...(item.nonChargeable ? { chargeableUnitCost: item.unitCost } : {}),
    }));
}

/**
 * The create payload for one line, resolved against the product catalog.
 *
 * Template-minted lines carry a SKU but no `productId` — `generateLineItemFromTemplate`
 * only reads the catalog for a price — and a line reaching RepairShopr without
 * one is a free-text charge with no product behind it. So this does what
 * `mapLineItemsToJobLineItems` does for ticket creation: resolve by SKU
 * (case-insensitively, as the catalog isn't self-consistent) and send back the
 * catalog's own spelling. A caller-supplied id still wins. sm4rt-api backstops
 * the same lookup server-side for the cold-cache case.
 */
export function buildLineItemCreate(item: LineItem): LineItemCreate {
  const product = getProductBySku(item.lineItemId);
  const productId = item.productId ?? product?.productId;
  return {
    name: product?.sku ?? item.lineItemId,
    quantity: item.quantity,
    price_retail: rsPriceRetail(item),
    description: item.lineDetail || item.productDescription || undefined,
    product_id: productId ? Number(productId) : undefined,
    templateKey: item.templateKey,
  };
}

export interface DiffLineItemsOptions {
  /**
   * Treat lines the user removed from the form as deletions. The Edit Ticket
   * page needs this (it has no per-row delete that fires immediately), while
   * the technician card deletes on the spot and passes false.
   */
  deleteMissing?: boolean;
  /**
   * The metadata blob as last read from Dataverse. The endpoint rewrites the
   * blob wholesale, so without a baseline every save would PATCH identical
   * content back.
   */
  previousOverrides?: LineItemOverride[];
}

export function diffLineItemsForEdit(
  formItems: LineItem[],
  rsItems: RepairshoprTicketLineItem[],
  options: DiffLineItemsOptions = {},
): LineItemEditDiff {
  const { deleteMissing = false, previousOverrides = [] } = options;

  // Converted lines have been invoiced. They are excluded at hydration, so
  // reaching one here means something upstream changed — never emit a write
  // against a line the customer has already been billed for.
  const editable = formItems.filter(item => !item.converted && !item.pending);

  const rsSnapshot = new Map(rsItems.map(rs => [rs.id, rs]));

  const creates: LineItemCreate[] = [];
  const updates: LineItemUpdate[] = [];

  for (const item of editable) {
    if (item.rsLineItemId != null) {
      const fields = getRsDirtyFields(item, rsSnapshot);
      if (fields) {
        updates.push({ rsLineItemId: item.rsLineItemId, fields });
      }
      continue;
    }

    // A line with no SKU is an untouched placeholder row, not a charge.
    if (!item.lineItemId) continue;

    // A suppressed or zeroed-out template line is not a charge either. It has
    // never reached RS (no rsLineItemId), so there is nothing to delete.
    if (item.source === 'ticketTypeTemplate' && item.quantity <= 0) continue;

    creates.push(buildLineItemCreate(item));
  }

  let deletes: number[] = [];
  if (deleteMissing) {
    const keptIds = new Set(
      formItems
        .filter(item => item.rsLineItemId != null)
        .map(item => item.rsLineItemId!),
    );
    deletes = rsItems
      .filter(rs => !rs.converted && !keptIds.has(rs.id))
      .map(rs => rs.id);
  }

  const overrides = buildOverrides(formItems.filter(item => !item.pending));

  return {
    creates,
    updates,
    deletes,
    overrides,
    isEmpty:
      creates.length === 0 &&
      updates.length === 0 &&
      deletes.length === 0 &&
      !haveOverridesChanged(overrides, previousOverrides),
  };
}

/**
 * Field-by-field comparison, keyed by RS line id and order-independent.
 *
 * A line with no stored entry is compared against the defaults rather than
 * counted as a change, because that is how the reader treats it — sm4rt-api
 * resolves a missing entry to exactly these values. Without that, every save
 * of an untouched ticket would rewrite the blob with identical content.
 */
function haveOverridesChanged(
  next: LineItemOverride[],
  previous: LineItemOverride[],
): boolean {
  const previousById = new Map(previous.map(o => [o.rsLineItemId, o]));

  const changed = next.some(o => {
    const before = previousById.get(o.rsLineItemId);
    return (
      (before?.afterHours ?? false) !== o.afterHours ||
      (before?.nonChargeable ?? false) !== o.nonChargeable ||
      (before?.prefixUserBase ?? '') !== o.prefixUserBase ||
      (before?.category ?? '') !== o.category ||
      (before?.templateKey ?? undefined) !== (o.templateKey ?? undefined) ||
      (before?.chargeableUnitCost ?? undefined) !== (o.chargeableUnitCost ?? undefined)
    );
  });
  if (changed) return true;

  // An entry that has dropped out belongs to a line that is going away. The
  // blob is rewritten wholesale, so it has to be resent to shed the row.
  const nextIds = new Set(next.map(o => o.rsLineItemId));
  return previous.some(o => !nextIds.has(o.rsLineItemId));
}
